import type {
	PitchedRoofConfig,
	RoofConfig,
	RoofKind,
} from "../../engines/types";
import { getT } from "../../i18n";
import type { SaunaType } from "../../types/design";
import type { Market } from "../location/types";
import {
	getRoofRunoffLabel,
	getRoofSystemLabel,
	normalizeRoofRunoffId,
	normalizeRoofSystemId,
} from "./roofSystems";
import { DEFAULT_MONO_HIGH_WALL, isRectWallName } from "./rectRoofGeometry";

export const ROOF_PITCH_LIMITS: Record<
	Exclude<RoofKind, "barrel">,
	{ min: number; max: number }
> = {
	mono: { min: 5, max: 14 },
	gable: { min: 14, max: 35 },
};

export const DEFAULT_ROOF_CONFIG_BY_TYPE: Record<SaunaType, RoofConfig> = {
	fyrkantig: {
		kind: "mono",
		pitchDeg: 8,
		systemId: "felt_roof",
		runoff: "free_drip",
	},
	tvarum: {
		kind: "gable",
		pitchDeg: 22,
		systemId: "felt_roof",
		runoff: "free_drip",
	},
	tunna: { kind: "barrel", pitchDeg: 0 },
	// Issue 156: an indoor sauna has a flat ceiling under the house's own
	// roof. Modelled as a zero-pitch mono so the rectangular roof geometry
	// (ceiling area, wall heights) needs no special case; nothing covers it.
	inomhus: {
		kind: "mono",
		pitchDeg: 0,
		systemId: "felt_roof",
		runoff: "free_drip",
	},
};

function isPitchedRoofConfig(config: RoofConfig): config is PitchedRoofConfig {
	return config.kind !== "barrel";
}

export function clampRoofPitch(kind: RoofKind, pitchDeg: number): number {
	if (kind === "barrel") {
		return 0;
	}

	const limits = ROOF_PITCH_LIMITS[kind];
	return Math.min(limits.max, Math.max(limits.min, Math.round(pitchDeg)));
}

export function normalizeRoofConfig(
	type: SaunaType,
	roofConfig?: Partial<RoofConfig> | null,
): RoofConfig {
	const defaults = DEFAULT_ROOF_CONFIG_BY_TYPE[type];
	const kind = roofConfig?.kind ?? defaults.kind;

	if (type === "tunna" || kind === "barrel") {
		return DEFAULT_ROOF_CONFIG_BY_TYPE.tunna;
	}
	// Not a choice indoors — and the zero pitch sits below the mono clamp.
	if (type === "inomhus") {
		return DEFAULT_ROOF_CONFIG_BY_TYPE.inomhus;
	}

	const resolvedKind = kind === "gable" ? "gable" : "mono";
	const pitchedDefaults: PitchedRoofConfig = isPitchedRoofConfig(defaults)
		? defaults
		: (DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig as PitchedRoofConfig);
	const fallbackPitch = roofConfig?.pitchDeg ?? pitchedDefaults.pitchDeg;

	const inputSystemId = (roofConfig as Partial<PitchedRoofConfig>)?.systemId;
	const inputHighWall = (roofConfig as Partial<PitchedRoofConfig>)?.highWall;

	return {
		kind: resolvedKind,
		pitchDeg: clampRoofPitch(resolvedKind, fallbackPitch),
		systemId: normalizeRoofSystemId(inputSystemId ?? pitchedDefaults.systemId),
		runoff: normalizeRoofRunoffId(),
		// Only a mono roof has a high side. The default (`front`) is stored as
		// ABSENT so pre-existing designs and share links stay byte-identical.
		...(resolvedKind === "mono" &&
		isRectWallName(inputHighWall) &&
		inputHighWall !== DEFAULT_MONO_HIGH_WALL
			? { highWall: inputHighWall }
			: {}),
	};
}

export function getRoofPitchLimits(kind: RoofKind) {
	return kind === "barrel" ? { min: 0, max: 0 } : ROOF_PITCH_LIMITS[kind];
}

export function getRoofKindLabel(
	kind: RoofKind,
	market: Market = "se",
): string {
	const t = getT(market);
	if (kind === "gable") return t.domain_roof_gable;
	if (kind === "barrel") return t.domain_roof_barrel;
	return t.domain_roof_mono;
}

export function getRoofConfigSummaryLabel(
	roofConfig: RoofConfig,
	market: Market = "se",
): string {
	if (roofConfig.kind === "barrel") {
		return getRoofKindLabel(roofConfig.kind, market);
	}
	// Only the indoor type (issue 156) has a zero pitch — the mono clamp starts
	// at 5°. It has a ceiling, not a roof: no covering or runoff to name.
	if (roofConfig.pitchDeg === 0) {
		return getT(market).domain_roof_indoorCeiling;
	}

	const systemId = normalizeRoofSystemId(roofConfig.systemId);
	const runoff = normalizeRoofRunoffId();

	return [
		`${getRoofKindLabel(roofConfig.kind, market)} • ${roofConfig.pitchDeg}°`,
		getRoofSystemLabel(systemId, market),
		getRoofRunoffLabel(runoff, market),
	].join(" • ");
}
